import { Badge, Card, Spin, Button, message, Modal } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined } from "@ant-design/icons";
import FilterPanel from "../hiring/FilterPanel";
import "../hiring/FilterPanel.css";
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useState, useMemo } from 'react';
import { MapContainer, Marker, Polygon, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from "react-redux";
import { CompanyOfficeDetailsGetbyCompanyId } from "../../redux/rtk/features/settingSlice/settingSlice";
import dayjs from 'dayjs';
import { fetchAttendanceByCompanyId, updateAttendanceAction } from "../../redux/rtk/features/attendance/attendanceSlice";
import { debounce } from 'lodash';
import getUserFromToken from "../../utils/getUserFromToken";
import { CSVLink } from 'react-csv';
import { CsvLinkBtn } from '../UI/CsvLinkBtn';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png",
});

// Predefined colors for employee pins
const PIN_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
  '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
  '#EE5A24', '#0984E3', '#6C5CE7', '#00B894', '#FDCB6E',
  '#E84393', '#2D3436', '#636E72', '#A29BFE', '#FD79A8'
];

// Function to create custom colored icon
const createColoredIcon = (color, isHighlighted = false) => {
  const size = isHighlighted ? [30, 30] : [25, 25];
  const anchor = isHighlighted ? [17, 55] : [12, 41];
  
  return new L.DivIcon({
    className: 'custom-div-icon',
    html: `<div style="
      background-color: ${color};
      width: ${size[0]}px;
      height: ${size[1]}px;
      border-radius: 50% 50% 50% 0;
      border: 3px solid white;
      transform: rotate(-45deg);
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      position: relative;
      ${isHighlighted ? 'animation: pulse 2s infinite;' : ''}
    ">
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(45deg);
        color: white;
        font-weight: bold;
        font-size: ${isHighlighted ? '16px' : '12px'};
        text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
      ">${isHighlighted ? '★' : '●'}</div>
    </div>
    <style>
      @keyframes pulse {
        0% { transform: rotate(-45deg) scale(1); }
        50% { transform: rotate(-45deg) scale(1.1); }
        100% { transform: rotate(-45deg) scale(1); }
      }
    </style>`,
    iconSize: size,
    iconAnchor: anchor,
    popupAnchor: [0, -size[1]]
  });
};

// MapEvents component to handle map clicks
const MapEvents = ({ setSelectedEmployee }) => {
  const map = useMapEvents({
    click: () => {
      setSelectedEmployee(null);
    },
  });
  return null;
};

const GeoAttendancePage = () => {
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [mapCenter, setMapCenter] = useState(null);
  const [map, setMap] = useState(null);
  const [officeAreas, setOfficeAreas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeColors, setEmployeeColors] = useState({});
  const [approvalStatus, setApprovalStatus] = useState({}); // Track approval status for each employee
  const [approving, setApproving] = useState({}); // Track which employees are being approved
  const [rejecting, setRejecting] = useState({}); // Track which employees are being rejected
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [locationFilter, setLocationFilter] = useState('all'); // 'all', 'inside', 'outside', 'outside-approved'
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  
  const dispatch = useDispatch();
  
  const companyOffices = useSelector((state) => state?.setting?.companyOfficeDetail);
  const mobileAttendance = useSelector((state) => state?.attendance?.mobileAttendance) || [];
  const loading = useSelector((state) => state?.attendance?.loading);
  const CompanyId = 1;

  // Fetch company office details
  useEffect(() => {
    dispatch(CompanyOfficeDetailsGetbyCompanyId(CompanyId));
  }, [dispatch]);

  // Fetch attendance data
  useEffect(() => {
    const fromDate = dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    const toDate = dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    dispatch(fetchAttendanceByCompanyId({ punchDate: fromDate, toDate }));
  }, [dispatch, dateRange]);

  // Process office areas and set map center
  useEffect(() => {
    if (companyOffices && Array.isArray(companyOffices)) {
      const areas = companyOffices
        .filter(office => office.CompanyOfficeArea)
        .map(office => ({
          id: office.CompanyOfficeId,
          name: office.CompanyOfficeName,
          coordinates: parsePolygon(office.CompanyOfficeArea),
        }))
        .filter(area => area.coordinates.length > 0);
      
      setOfficeAreas(areas);
      
      if (areas.length > 0 && areas[0].coordinates.length > 0) {
        const firstArea = areas[0].coordinates;
        const latSum = firstArea.reduce((sum, point) => sum + point[0], 0);
        const lngSum = firstArea.reduce((sum, point) => sum + point[1], 0);
        setMapCenter([
          latSum / firstArea.length,
          lngSum / firstArea.length
        ]);
      } else {
        setMapCenter([0, 0]);
      }
    }
  }, [companyOffices]);

  // Assign colors to employees
  useEffect(() => {
    if (mobileAttendance && Array.isArray(mobileAttendance)) {
      const uniqueEmployees = [...new Set(mobileAttendance.map(record => record.EmployeeName))];
      const colors = {};
      uniqueEmployees.forEach((employeeName, index) => {
        colors[employeeName] = PIN_COLORS[index % PIN_COLORS.length];
      });
      setEmployeeColors(colors);
    }
  }, [mobileAttendance]);

  // Navigate to selected employee's location
  useEffect(() => {
    if (
      map &&
      selectedEmployee &&
      selectedEmployee.Latitude &&
      selectedEmployee.Longitude &&
      !isNaN(selectedEmployee.Latitude) &&
      !isNaN(selectedEmployee.Longitude)
    ) {
      map.setView([selectedEmployee.Latitude, selectedEmployee.Longitude], 15, {
        animate: true,
        duration: 1,
      });
    }
  }, [map, selectedEmployee]);

  // Get unique employees with their last punch data
  const uniqueEmployees = useMemo(() => {
    if (!mobileAttendance || !Array.isArray(mobileAttendance)) return [];
    
    const employeeMap = new Map();
    
    mobileAttendance.forEach(record => {
      if (record.EmployeeName && record.Latitude && record.Longitude) {
        const existing = employeeMap.get(record.EmployeeName);
        if (!existing || new Date(record.PunchDateTime) > new Date(existing.PunchDateTime)) {
          employeeMap.set(record.EmployeeName, record);
        }
      }
    });
    
    return Array.from(employeeMap.values());
  }, [mobileAttendance]);

  // Parse POLYGON string
  const parsePolygon = (polygonString) => {
    if (!polygonString || !polygonString.startsWith('POLYGON ((')) return [];
    const coordsString = polygonString.replace('POLYGON ((', '').replace('))', '');
    const coordPairs = coordsString.split(', ').map(pair => {
      const [lon, lat] = pair.split(' ').map(Number);
      return [lat, lon];
    });
    return coordPairs;
  };

  // Check if point is in office area
  const isPointInOfficeArea = (lat, lng) => {
    for (const area of officeAreas) {
      if (isPointInPolygon([lat, lng], area.coordinates)) {
        return { inArea: true, officeName: area.name };
      }
    }
    return { inArea: false, officeName: null };
  };

  // Ray casting algorithm
  const isPointInPolygon = (point, polygon) => {
    if (polygon.length === 0) return false;
    let inside = false;
    const x = point[0];
    const y = point[1];
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      
      const intersect = ((yi > y) !== (yj > y))
          && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    
    return inside;
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange([dayjs(), dayjs()]);
    }
  };

  // For FilterPanel: date range as [start, end] or null
  const handleDateRangeFromPanel = (dates) => {
    if (dates && dates[0] != null && dates[1] != null) setDateRange(dates);
    else setDateRange([dayjs(), dayjs()]);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setDateRange([dayjs(), dayjs()]);
    setSearchTerm('');
    setLocationFilter('all');
  };

  // Handle employee selection with debounce
  const handleEmployeeClick = debounce((employee) => {
    setSelectedEmployee(employee);
  }, 300);

  // API call to approve outside office attendance
  const approveOutsideOfficeAttendance = async (employeeId, attendanceId) => {
    try {
      // console.log('Approving outside office attendance:', { employeeId, attendanceId });
      
      const response = await axios.post('/attendance/ApproveOutsideOfficeAttendance', {
        EmployeeId: employeeId,
        AttendanceId: attendanceId,
        IsApproved: 1, // Pass 1 for approval
        ApprovedDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        ApprovedBy: 'Admin', // You can get this from user context
        Remarks: 'Approved by Admin - Outside Office Attendance'
      });

      if (response.data && response.data.message === 'success') {
        message.success('Attendance approved successfully!');
        return true;
      } else {
        message.error('Failed to approve attendance');
        return false;
      }
    } catch (error) {
      // console.error('Error approving attendance:', error);
      message.error('Error approving attendance. Please try again.');
      return false;
    }
  };

  // Handle approve/reject button click
  const handleActionClick = (employee, action) => {
    setPendingEmployee(employee);
    setActionType(action);
    setConfirmModalVisible(true);
  };

  // Handle confirm action
  const handleConfirmAction = async () => {
    if (!pendingEmployee || !actionType) return;

    const employeeKey = `${pendingEmployee.EmployeeId}_${pendingEmployee.PunchDateTime}`;
    const actionStatusId = actionType === 'approve' ? 1 : 2;
    
    // Set loading state
    if (actionType === 'approve') {
      setApproving(prev => ({ ...prev, [employeeKey]: true }));
    } else {
      setRejecting(prev => ({ ...prev, [employeeKey]: true }));
    }
    
    try {
      const result = await dispatch(updateAttendanceAction({
        EmployeeId: pendingEmployee.EmployeeId,
        ActionStatusId: actionStatusId,
        PunchDateTime: pendingEmployee.PunchDateTime
      }));

      if (result.payload.message === 'success') {
        // Update status in local state
        setApprovalStatus(prev => ({ 
          ...prev, 
          [employeeKey]: { 
            approved: actionType === 'approve', 
            rejected: actionType === 'reject',
            actionAt: dayjs().format('YYYY-MM-DD HH:mm:ss') 
          } 
        }));
      }
    } catch (error) {
      // console.error('Error updating attendance action:', error);
    } finally {
      // Remove loading state
      if (actionType === 'approve') {
        setApproving(prev => {
          const newState = { ...prev };
          delete newState[employeeKey];
          return newState;
        });
      } else {
        setRejecting(prev => {
          const newState = { ...prev };
          delete newState[employeeKey];
          return newState;
        });
      }
      setConfirmModalVisible(false);
      setPendingEmployee(null);
      setActionType(null);
    }
  };

  // Filter attendance records based on search term
  const filteredAttendance = mobileAttendance.filter(record =>
    record.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter employees based on search term and location filter
  const filteredEmployees = uniqueEmployees.filter(employee => {
    const matchesSearch = employee.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Apply location filter
    const locationStatus = isPointInOfficeArea(employee.Latitude, employee.Longitude);
    const employeeKey = `${employee.EmployeeId}_${employee.PunchDateTime}`;
    const isApproved = approvalStatus[employeeKey]?.approved || false;
    const isServerApproved = employee.ActionStatusName === 'Approved';
    const isServerPending = employee.ActionStatusName === 'Pending';
    
    switch (locationFilter) {
      case 'inside':
        return locationStatus.inArea;
      case 'outside':
        return !locationStatus.inArea && !isApproved && !isServerApproved;
      case 'outside-approved':
        return !locationStatus.inArea && (isApproved || isServerApproved);
      default: // 'all'
        return true;
    }
  });

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return dayjs(timeString).format('hh:mm A');
  };

  // Fit map bounds
  const fitMapBounds = () => {
    if (!map || !filteredEmployees || !Array.isArray(filteredEmployees)) return;
    
    const bounds = L.latLngBounds([]);
    officeAreas.forEach(area => {
      if (Array.isArray(area.coordinates) && area.coordinates.length > 0) {
        area.coordinates.forEach(coord => {
          bounds.extend(L.latLng(coord[0], coord[1]));
        });
      }
    });
    filteredEmployees.forEach(record => {
      if (record.Latitude && record.Longitude && !isNaN(record.Latitude) && !isNaN(record.Longitude)) {
        bounds.extend(L.latLng(record.Latitude, record.Longitude));
      }
    });
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  useEffect(() => {
    if (map && filteredEmployees && officeAreas.length > 0) {
      fitMapBounds();
    }
  }, [map, filteredEmployees, officeAreas, locationFilter]);

  return (
    <div className="geo-attendance-page-container">
      <div className="geo-attendance-content-container">
        {/* Combined Summary and Filter Section */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
           <Card className="w-full">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-semibold text-gray-700">Attendance Summary</h3>
               <div className="flex gap-2 items-center">
                 <div className="filter-panel-wrap" style={{ position: "relative" }}>
                   <Badge
                     count={
                       (dateRange[0] && dateRange[1] && (dateRange[0].format("YYYY-MM-DD") !== dayjs().format("YYYY-MM-DD") || dateRange[1].format("YYYY-MM-DD") !== dayjs().format("YYYY-MM-DD")) ? 1 : 0) +
                       (searchTerm.trim() ? 1 : 0) +
                       (locationFilter !== "all" ? 1 : 0)
                     }
                     size="small"
                     offset={[-2, 2]}
                   >
                     <button
                       type="button"
                       className="filter-panel-trigger-blue"
                       onClick={() => setFilterPanelOpen((o) => !o)}
                       aria-label="Filters"
                     >
                       <FilterOutlined style={{ fontSize: 16 }} />
                     </button>
                   </Badge>
                   <FilterPanel
                     open={filterPanelOpen}
                     onClose={() => setFilterPanelOpen(false)}
                     dateRange={dateRange}
                     onDateRangeChange={handleDateRangeFromPanel}
                     onClearAll={handleClearFilters}
                     search={{
                       placeholder: "Search by employee name",
                       value: searchTerm,
                       onChange: (v) => setSearchTerm(v ?? ""),
                     }}
                     filters={[
                       {
                         key: "location",
                         label: "Location Status",
                         placeholder: "All",
                         value: locationFilter,
                         onChange: setLocationFilter,
                         options: [
                           { value: "all", label: "All" },
                           { value: "inside", label: "In Office Area" },
                           { value: "outside", label: "Outside Office (Pending)" },
                           { value: "outside-approved", label: "Outside Office (Approved)" },
                         ],
                       },
                     ]}
                   />
                 </div>
                 {filteredAttendance?.length > 0 && (
                   <CsvLinkBtn>
                     <CSVLink
                       data={filteredAttendance.map((record, index) => ({
                         'S.No': index + 1,
                         'Employee Name': record.EmployeeName || 'N/A',
                         'Punch Date Time': record.PunchDateTime ? dayjs(record.PunchDateTime).format('DD-MM-YYYY HH:mm:ss') : 'N/A',
                         'Punch Location': record.PunchLocation || 'N/A',
                         'Latitude': record.Latitude || 'N/A',
                         'Longitude': record.Longitude || 'N/A',
                         'Action Status': record.ActionStatusName || 'N/A',
                         'Is Punch Valid': record.IsPunchValid ? 'Yes' : 'No',
                         'Location Status': isPointInOfficeArea(record.Latitude, record.Longitude).inArea ? 'Inside Office' : 'Outside Office'
                       }))}
                       filename={`geo_attendance_${dayjs().format('YYYY-MM-DD')}.csv`}
                       className="btn btn-dark btn-sm"
                       style={{ marginTop: "5px" }}
                     >
                       Download CSV
                     </CSVLink>
                   </CsvLinkBtn>
                 )}
                 {locationFilter !== 'all' && (
                   <Button 
                     size="small" 
                     onClick={() => setLocationFilter('all')}
                     className="text-gray-600 hover:text-gray-800"
                   >
                     Clear Filter
                   </Button>
                 )}
               </div>
             </div>
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <Spin size="large" />
                <span className="ml-2">Loading attendance data...</span>
              </div>
            ) : (
               <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                 <div 
                   className={`p-3 rounded-md cursor-pointer transition-all duration-200 border-2 ${
                     locationFilter === 'inside' 
                       ? 'border-green-500 bg-green-200 shadow-md' 
                       : 'border-green-200 bg-green-100 hover:bg-green-150 hover:border-green-300'
                   }`}
                   onClick={() => setLocationFilter('inside')}
                 >
                   <div className="font-bold text-green-700 text-sm">In Office Area</div>
                   <div className="text-lg font-semibold">
                     {uniqueEmployees?.filter(record => 
                       record.Latitude && record.Longitude && 
                       !isNaN(record.Latitude) && !isNaN(record.Longitude) &&
                       isPointInOfficeArea(record.Latitude, record.Longitude).inArea
                     )?.length || 0}
                   </div>
                 </div>
                 <div 
                   className={`p-3 rounded-md cursor-pointer transition-all duration-200 border-2 ${
                     locationFilter === 'outside' 
                       ? 'border-orange-500 bg-orange-200 shadow-md' 
                       : 'border-orange-200 bg-orange-100 hover:bg-orange-150 hover:border-orange-300'
                   }`}
                   onClick={() => setLocationFilter('outside')}
                 >
                   <div className="font-bold text-orange-700 text-sm">Outside Office (Pending)</div>
                   <div className="text-lg font-semibold">
                     {uniqueEmployees?.filter(record => {
                       const locationStatus = isPointInOfficeArea(record.Latitude, record.Longitude);
                       const employeeKey = `${record.EmployeeId}_${record.PunchDateTime}`;
                       const isApproved = approvalStatus[employeeKey]?.approved || false;
                       const isServerApproved = record.ActionStatusName === 'Approved';
                       return record.Latitude && record.Longitude && 
                         !isNaN(record.Latitude) && !isNaN(record.Longitude) &&
                         !locationStatus.inArea && !isApproved && !isServerApproved;
                     })?.length || 0}
                   </div>
                 </div>
                 <div 
                   className={`p-3 rounded-md cursor-pointer transition-all duration-200 border-2 ${
                     locationFilter === 'outside-approved' 
                       ? 'border-green-500 bg-green-200 shadow-md' 
                       : 'border-green-200 bg-green-100 hover:bg-green-150 hover:border-green-300'
                   }`}
                   onClick={() => setLocationFilter('outside-approved')}
                 >
                   <div className="font-bold text-green-700 text-sm">Outside Office (Approved)</div>
                   <div className="text-lg font-semibold">
                     {uniqueEmployees?.filter(record => {
                       const locationStatus = isPointInOfficeArea(record.Latitude, record.Longitude);
                       const employeeKey = `${record.EmployeeId}_${record.PunchDateTime}`;
                       const isApproved = approvalStatus[employeeKey]?.approved || false;
                       const isServerApproved = record.ActionStatusName === 'Approved';
                       return record.Latitude && record.Longitude && 
                         !isNaN(record.Latitude) && !isNaN(record.Longitude) &&
                         !locationStatus.inArea && (isApproved || isServerApproved);
                     })?.length || 0}
                   </div>
                 </div>
                 <div 
                   className={`p-3 rounded-md cursor-pointer transition-all duration-200 border-2 ${
                     locationFilter === 'all' 
                       ? 'border-blue-500 bg-blue-200 shadow-md' 
                       : 'border-gray-200 bg-gray-100 hover:bg-gray-150 hover:border-gray-300'
                   }`}
                   onClick={() => setLocationFilter('all')}
                 >
                   <div className="font-bold text-gray-700 text-sm">Total Employees</div>
                   <div className="text-lg font-semibold">
                     {uniqueEmployees?.length || 0}
                   </div>
                 </div>
               </div>
            )}
          </Card>
        </div>

        {/* Map and Employee List Section */}
        <div className="flex flex-col lg:flex-row  geo-attendance-map-section">
          {/* Map Section */}
          <div className="flex-1 geo-attendance-map-wrapper">
            {mapCenter ? (
              <MapContainer
                style={{ width: '100%', height: '100%', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                center={mapCenter}
                zoom={10}
                whenCreated={setMap}
              >
                <TileLayer
                  attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapEvents setSelectedEmployee={setSelectedEmployee} />
                {officeAreas.map((area, idx) => (
                  <Polygon 
                    key={`office-area-${idx}`}
                    positions={area.coordinates}
                    pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
                  >
                    <Popup>
                      <strong>{area.name}</strong>
                      <p>Office Area</p>
                    </Popup>
                  </Polygon>
                ))}
                {!loading && filteredEmployees && Array.isArray(filteredEmployees) && filteredEmployees.map((record, idx) => {
                  if (!record.Latitude || !record.Longitude || isNaN(record.Latitude) || isNaN(record.Longitude)) return null;
                  const locationStatus = isPointInOfficeArea(record.Latitude, record.Longitude);
                  const employeeColor = employeeColors[record.EmployeeName] || PIN_COLORS[0];
                  const isHighlighted = selectedEmployee && selectedEmployee.EmployeeName === record.EmployeeName;
                  const markerIcon = createColoredIcon(employeeColor, isHighlighted);
                  
                  return (
                    <Marker 
                      key={`attendance-${idx}`}
                      position={[record.Latitude, record.Longitude]}
                      icon={markerIcon}
                      ref={(ref) => {
                        if (ref && isHighlighted) {
                          ref.openPopup();
                        }
                      }}
                    >
                      <Popup>
                        <div className="attendance-popup">
                          <h3 className="font-bold">{record.EmployeeName || 'Employee'}</h3>
                          <p><strong>Last Punch:</strong> {formatTime(record.PunchDateTime)}</p>
                          <p><strong>Location:</strong> {locationStatus.inArea ? 
                            <span className="text-green-600">Inside {locationStatus.officeName}</span> : 
                            <span className="text-red-600">Outside Office Area</span>}
                          </p>
                          {record.ActionStatusName && (
                            <p><strong>Action Status:</strong> <span className="font-medium text-blue-600">{record.ActionStatusName}</span></p>
                          )}
                           {!locationStatus.inArea && (approvalStatus[`${record.EmployeeId}_${record.PunchDateTime}`]?.approved || record.ActionStatusName === 'Approved') && (
                             <p><strong>Status:</strong> <span className="text-green-600 font-bold">✓ Approved</span></p>
                           )}
                          <p><strong>Punch Location:</strong> {record.PunchLocation}</p>
                          <p><strong>Coordinates:</strong> {record.Latitude}, {record.Longitude}</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="flex justify-center items-center h-full bg-white rounded-lg shadow-md">
                <Spin size="large" />
                <span className="ml-2">Loading office location...</span>
              </div>
            )}
          </div>

          {/* Employee List Sidebar */}
          <div className="w-full lg:w-80 geo-attendance-employee-list">
            <Card className="h-full">
               <div className="flex justify-between items-center mb-4">
                 <div>
                   <h3 className="text-lg font-semibold text-gray-700">Employees ({filteredEmployees.length})</h3>
                   {locationFilter !== 'all' && (
                     <div className="text-xs text-blue-600 font-medium">
                       Filtered by: {
                         locationFilter === 'inside' ? 'In Office Area' :
                         locationFilter === 'outside' ? 'Outside Office (Pending)' :
                         locationFilter === 'outside-approved' ? 'Outside Office (Approved)' : 'All'
                       }
                     </div>
                   )}
                 </div>
                 {selectedEmployee && (
                   <Button 
                     size="small" 
                     onClick={() => setSelectedEmployee(null)}
                     className="text-gray-500"
                   >
                     Clear Selection
                   </Button>
                 )}
               </div>
              <div className="employee-list-container">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <Spin size="large" />
                  </div>
                ) : filteredEmployees.length > 0 ? (
                  <div className="employee-list-items">
                    {filteredEmployees.map((employee, idx) => {
                      const locationStatus = isPointInOfficeArea(employee.Latitude, employee.Longitude);
                      const employeeColor = employeeColors[employee.EmployeeName] || PIN_COLORS[0];
                      const isSelected = selectedEmployee && selectedEmployee.EmployeeName === employee.EmployeeName;
                       const employeeKey = `${employee.EmployeeId}_${employee.PunchDateTime}`;
                       const isApproved = approvalStatus[employeeKey]?.approved || false;
                       const isServerApproved = employee.ActionStatusName === 'Approved';
                       const isServerPending = employee.ActionStatusName === 'Pending';
                       const isApproving = approving[employeeKey] || false;
                       const isOutsideOffice = !locationStatus.inArea;
                      
                      return (
                        <div
                          key={`employee-${idx}`}
                           className={`p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 ${
                             isSelected 
                               ? 'border-blue-500 bg-blue-50 shadow-md' 
                               : (isApproved || isServerApproved) && isOutsideOffice
                               ? 'border-green-500 bg-green-50 shadow-md'
                               : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                           }`}
                          onClick={() => handleEmployeeClick(employee)}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full border-2 border-white shadow-md"
                              style={{ backgroundColor: employeeColor }}
                            ></div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-800 truncate">
                                {employee.EmployeeName}
                              </div>
                              <div className="text-sm text-gray-600">
                                Last Punch: {formatTime(employee.PunchDateTime)}
                              </div>
                              <div className="flex items-center justify-between">
                                 <div className={`text-xs font-medium ${
                                   locationStatus.inArea ? 'text-green-600' : 'text-red-600'
                                 }`}>
                                   {locationStatus.inArea ? 
                                     `✓ In ${locationStatus.officeName}` : 
                                     '✗ Outside Office'
                                   }
                                 </div>
                                {/* Approve/Reject buttons based on conditions */}
                                {(() => {
                                  const loggedEmployeeId = getUserFromToken();
                                  const shouldShowButtons = 
                                    (isServerPending || (!isServerApproved && !isApproved)) &&
                                    employee.EmployeeId !== loggedEmployeeId &&
                                    employee.IsPunchValid === false &&
                                    !approvalStatus[employeeKey]?.rejected;
                                  
                                  if (!shouldShowButtons) return null;
                                  
                                  return (
                                    <div className="flex gap-1">
                                      <Button
                                        size="small"
                                        icon={<CheckOutlined />}
                                        loading={isApproving}
                                        disabled={isApproving || rejecting[employeeKey]}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleActionClick(employee, 'approve');
                                        }}
                                        style={{
                                          backgroundColor: '#52c41a',
                                          borderColor: '#52c41a',
                                          color: 'white',
                                          fontSize: '10px',
                                          height: '20px',
                                          padding: '0 6px',
                                          minWidth: '24px',
                                        }}
                                        title="Approve"
                                      />
                                      <Button
                                        size="small"
                                        icon={<CloseOutlined />}
                                        loading={rejecting[employeeKey]}
                                        disabled={rejecting[employeeKey] || isApproving}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleActionClick(employee, 'reject');
                                        }}
                                        style={{
                                          backgroundColor: '#ff4d4f',
                                          borderColor: '#ff4d4f',
                                          color: 'white',
                                          fontSize: '10px',
                                          height: '20px',
                                          padding: '0 6px',
                                          minWidth: '24px',
                                        }}
                                        title="Reject"
                                      />
                                    </div>
                                  );
                                })()}
                                 {(isApproved || isServerApproved) && isOutsideOffice && (
                                   <div className="text-green-600 font-bold text-xs bg-green-100 px-2 py-1 rounded">
                                     ✓ Approved
                                   </div>
                                 )}
                               </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isSelected && (
                                <div className="text-blue-500 font-bold">★</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-gray-500 text-center p-4">
                    No employees found for the selected criteria.
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        title={`${actionType === 'approve' ? 'Approve' : 'Reject'} Attendance`}
        open={confirmModalVisible}
        onOk={handleConfirmAction}
        onCancel={() => {
          setConfirmModalVisible(false);
          setPendingEmployee(null);
          setActionType(null);
        }}
        okText={actionType === 'approve' ? 'Approve' : 'Reject'}
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: actionType === 'approve' ? '#52c41a' : '#ff4d4f',
            borderColor: actionType === 'approve' ? '#52c41a' : '#ff4d4f',
          }
        }}
      >
        <p>
          Are you sure you want to <strong>{actionType}</strong> the attendance for{' '}
          <strong>{pendingEmployee?.EmployeeName}</strong>?
        </p>
        <p className="text-gray-600 text-sm mt-2">
          Punch Time: {pendingEmployee?.PunchDateTime ? formatTime(pendingEmployee.PunchDateTime) : 'N/A'}
        </p>
        {pendingEmployee?.ActionStatusName && (
          <p className="text-gray-600 text-sm">
            Current Status: <span className="font-medium">{pendingEmployee.ActionStatusName}</span>
          </p>
        )}
      </Modal>
    </div>
  );
};

export default GeoAttendancePage;